'use client'

import { useTranslations } from 'next-intl'

/**
 * Hook to translate tag/topic IDs to display labels
 * Tags/topics are stored as technical identifiers in content files (for filtering)
 * This hook provides translated display labels
 */
export function useTagTranslations() {
  const t = useTranslations('tags')
  const translate = t as (key: string) => string

  /**
   * Translate a blog tag ID to display label
   * @param tagId - Technical tag ID from blog frontmatter (e.g., 'learning', 'beginner')
   * @returns Translated display label, or the original tagId if no translation exists
   */
  const translateBlogTag = (tagId: string): string => {
    try {
      const translated = translate(`blog.${tagId}`)
      return translated || tagId
    } catch {
      return tagId
    }
  }

  /**
   * Translate a story topic ID to display label
   * @param topicId - Technical topic ID from story JSON (e.g., 'greetings', 'daily-life')
   * @returns Translated display label, or the original topicId if no translation exists
   */
  const translateStoryTopic = (topicId: string): string => {
    // Skip empty or invalid topic IDs
    if (!topicId || topicId.trim() === '') {
      return topicId
    }
    try {
      const translated = translate(`story.${topicId}`)
      return translated || topicId
    } catch {
      return topicId
    }
  }

  /**
   * Get story level ID - levels are not translated, shown with uppercase CSS
   * @param levelId - Technical level ID from story JSON (e.g., 'hsk-1', 'tocfl-2')
   * @returns The original levelId (will be styled with uppercase CSS in components)
   */
  const translateLevel = (levelId: string): string => {
    return levelId
  }

  return {
    translateBlogTag,
    translateStoryTopic,
    translateLevel,
    translateBlogTags: (tagIds: string[]) => tagIds.map(translateBlogTag),
    translateStoryTopics: (topicIds: string[]) =>
      topicIds.filter((id) => id && id.trim() !== '').map(translateStoryTopic)
  }
}
